// =====================================================================
// src/lib/lista.js — a Lista de Reservas do dono, sem DOM
//   (MdM `renderReservas` 2528 · `verificarConflito` 6977 ·
//    `_filtrarRelatorio` 4630 · `gerarRelatorioReservasExcel/PDF` 4650)
//
// A conta da tela da imobiliária: o período DE/ATÉ montado de três
// selects (ano, mês, dia), o filtro por situação, o total do período (só
// quem tem `financeiro` vê), a detecção GLOBAL de overbooking e as
// linhas do relatório PDF/Excel.
//
// Overbooking (decisão do Hugo, 21/09/2026, "1B"): a imobiliária PODE
// ter duas confirmadas no mesmo flat com datas que se cruzam (0050); a
// tela avisa — banner vermelho + badge na linha — e ela marca o par como
// "sobreposta intencionalmente" (marcação mútua em `sobreposta_com`), o
// que faz o aviso sumir. O hotel continua com a trava da 0015, então
// para ele nada disto dispara.
// =====================================================================
import { noites } from './reservas.js';
import { saoSobrepostas } from './quadro.js';

export const MESES_CURTOS = Object.freeze(['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']);
export const DIAS = Object.freeze(Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0')));
// `res-stat`: Todos · Confirmada · Pendente · Concluída · Cancelada. "Pendente"
// é da pré-reserva por WhatsApp (fora, §7); aqui entra `noshow`, que existe.
export const SITUACOES = Object.freeze([
  { chave: '', rotulo: 'Todos' },
  { chave: 'confirmada', rotulo: 'Confirmada' },
  { chave: 'concluida', rotulo: 'Concluída' },
  { chave: 'noshow', rotulo: 'No-show' },
  { chave: 'cancelada', rotulo: 'Cancelada' },
]);

// O filtro com que a tela abre (2541): do dia 01 ao 31 do mês corrente.
export function filtroPadrao(hoje) {
  const ano = hoje.slice(0, 4), mes = hoje.slice(5, 7);
  return { anoIni: ano, mesIni: mes, diaIni: '01', anoFim: ano, mesFim: mes, diaFim: '31', situacao: '' };
}
// DE/ATÉ como ISO, com o "fim antes do início" corrigido como lá (2551),
// e os rótulos dd/mm/aaaa → dd/mm/aaaa.
export function periodoDe(f) {
  const ini = `${f.anoIni}-${f.mesIni}-${f.diaIni}`;
  let fim = `${f.anoFim}-${f.mesFim}-${f.diaFim}`;
  if (fim < ini) fim = ini;
  return { ini, fim, rotuloDe: `${f.diaIni}/${f.mesIni}/${f.anoIni}`, rotuloAte: `${f.diaFim}/${f.mesFim}/${f.anoFim}`,
    rotulo: `${f.diaIni}/${f.mesIni}/${f.anoIni} → ${f.diaFim}/${f.mesFim}/${f.anoFim}` };
}
// Um ano válido para o input number (2000–2099); fora disso, o de antes.
export function anoValido(v, antes) {
  const n = Number(v);
  return Number.isInteger(n) && n >= 2000 && n <= 2099 ? String(n) : antes;
}

// As reservas que CRUZAM o período (2558): check-in até o fim e
// check-out desde o início; pela situação, se pedida; por check-in.
export function filtrar(reservas, f) {
  const p = periodoDe(f);
  return (reservas || []).filter(r => {
    if (!r.check_in) return false;
    const co = r.check_out || r.check_in;
    if (!(r.check_in <= p.fim && co >= p.ini)) return false;
    if (f.situacao && r.situacao !== f.situacao) return false;
    return true;
  }).sort((a, b) => String(a.check_in).localeCompare(String(b.check_in)));
}
// O total do período (2564): o valor INTEIRO de cada reserva que cruza o
// período, menos as canceladas — não é pro-rata, o Financeiro é que rateia.
export function totalDoPeriodo(lista, valorDe) {
  return lista.filter(r => r.situacao !== 'cancelada').reduce((s, r) => s + (Number(valorDe(r)) || 0), 0);
}

// ── overbooking ──────────────────────────────────────────────────────
// `verificarConflito` (6977): as outras reservas ativas do mesmo quarto
// cujas datas cruzam as desta — menos as marcadas como sobrepostas com ela.
export function conflitosDe(reservas, r) {
  if (!r || !r.unidade_id || !r.check_in || !r.check_out) return [];
  return (reservas || []).filter(o => {
    if (o.id === r.id || o.unidade_id !== r.unidade_id || o.situacao === 'cancelada') return false;
    if (!(o.check_in < r.check_out && o.check_out > r.check_in)) return false;
    return !saoSobrepostas(r, o);
  });
}
// A detecção GLOBAL (2567): todos os pares em conflito, cada par uma vez.
export function paresDeOverbooking(reservas) {
  const pares = [], vistos = new Set();
  for (const r of reservas || []) {
    if (r.situacao === 'cancelada' || !r.check_in || !r.check_out) continue;
    for (const c of conflitosDe(reservas, r)) {
      const k = [r.id, c.id].sort().join('|');
      if (vistos.has(k)) continue;
      vistos.add(k); pares.push({ a: r, b: c });
    }
  }
  return pares;
}
// O resumo do banner vermelho (2587): conflitos, reservas afetadas e os
// flats, em ordem numérica.
export function resumoDoOverbooking(pares, unidades) {
  const porFlat = new Map();
  for (const p of pares) for (const r of [p.a, p.b]) { if (!porFlat.has(r.unidade_id)) porFlat.set(r.unidade_id, new Set()); porFlat.get(r.unidade_id).add(r.id); }
  const numero = id => { const u = (unidades || []).find(x => x.id === id); return u ? u.numero : '?'; };
  const flats = [...porFlat.keys()].map(numero).sort((a, b) => (parseInt(a, 10) || 0) - (parseInt(b, 10) || 0));
  const reservasAfetadas = [...porFlat.values()].reduce((s, x) => s + x.size, 0);
  return { conflitos: pares.length, reservas: reservasAfetadas, flats };
}
// As reservas com que esta está marcada como sobreposta E que ainda existem
// e confirmam a marcação (2692).
export function sobrepostasDe(reservas, r) {
  return (r.sobreposta_com || []).map(id => (reservas || []).find(x => x.id === id)).filter(o => o && saoSobrepostas(r, o));
}
// Antes de marcar: as duas são do mesmo quarto, ativas e se cruzam — a
// mesma regra de `app.par_sobreposto` (0050), para a tela recusar antes
// do banco.
export function podeMarcar(a, b) {
  if (!a || !b || a.id === b.id) return 'Informe duas reservas diferentes.';
  if (a.unidade_id !== b.unidade_id) return 'As duas reservas não são do mesmo quarto.';
  if (a.situacao === 'cancelada' || b.situacao === 'cancelada') return 'Reserva cancelada não entra em sobreposição.';
  if (!(a.check_in < b.check_out && a.check_out > b.check_in)) return 'As datas dessas reservas não se cruzam.';
  return null;
}

// ── o relatório (4630, 4650, 4724) ───────────────────────────────────
// As linhas do Excel: Hóspede, E-mail, Telefone, Flat, Check-in,
// Check-out, Noites, Plataforma, Valor, Status — e a linha TOTAL (exceto
// canceladas). O PDF é o mesmo sem E-mail/Telefone. Valor só com
// `financeiro` (INVARIANTE 2): sem ele a coluna nem existe.
export function relatorio(reservas, f, { valorDe, comValor, quartoDe, hospedeDe, contatoDe, dataBR, formato = 'excel' }) {
  const p = periodoDe({ ...f, situacao: '' });
  const lista = filtrar(reservas, { ...f, situacao: '' });
  const excel = formato === 'excel';
  const dinheiro = v => (Number(v) || 0).toFixed(2).replace('.', ',');
  const colunas = ['Hóspede', ...(excel ? ['E-mail', 'Telefone'] : []), 'Flat', 'Check-in', 'Check-out', 'Noites', 'Plataforma', ...(comValor ? [excel ? 'Valor (R$)' : 'Valor'] : []), 'Status'];
  let total = 0;
  const linhas = lista.map(r => {
    const v = Number(valorDe(r)) || 0;
    if (r.situacao !== 'cancelada') total += v;
    const c = excel ? (contatoDe ? contatoDe(r) : {}) : null;
    return [hospedeDe(r) || '-', ...(excel ? [c.email || '', c.telefone || ''] : []), 'Flat ' + quartoDe(r), dataBR(r.check_in), dataBR(r.check_out),
      noites(r.check_in, r.check_out), r.plataforma || '-', ...(comValor ? [excel ? dinheiro(v) : 'R$ ' + dinheiro(v)] : []), r.situacao || '-'];
  });
  const rodape = comValor ? colunas.map((c, i) => (i === 0 ? 'TOTAL (exceto canceladas)' : c.startsWith('Valor') ? (excel ? dinheiro(total) : 'R$ ' + dinheiro(total)) : '')) : null;
  return { colunas, linhas, rodape, total, periodo: p, arquivo: `Reservas_${p.ini}_a_${p.fim}` };
}
